/**
 * Listing Wizard Redux Slice
 * 
 * SOLID Principles Applied:
 * - Single Responsibility: Only manages listing wizard state (reducers)
 * - Separation of Concerns: 
 *   • State management (this file)
 *   • Async operations (listingWizardThunks.ts)
 *   • Data access (listingWizardSelectors.ts)
 * - Open/Closed: Easy to add new reducers without modifying existing ones
 * - Dependency Inversion: Depends on thunks abstraction, not implementation
 * 
 * This file contains:
 * - Initial state definition
 * - Synchronous reducers
 * - Async thunk handlers (extraReducers)
 * - Slice export
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { migrateMockupsToFitGenderStructure } from '../../../utils/mockupMigration';
import { normalizePublishDistribution } from '../../../pages/seller/ListingWizard/utils/seoUtils';
import { normalizeListingSlug } from '../../../pages/seller/ListingWizard/utils/listingSlugUtils';
import {
  IListingWizardState,
  ListingWizardSnapshot,
  IListingWizardSection,
  IListingWizardSectionData,
  ListingCatalogSection,
  ListingVisualsSection,
} from '../../../types/listing-wizard.types';
import { purgeVisualsForRemovedProduct } from '../../../pages/seller/ListingWizard/utils/galleryCleanup';
import { removeCustomMockupUrlFromTree } from '../../../pages/seller/ListingWizard/utils/customMockupCleanup';

// Import async thunks (Separation of Concerns)
import {
  fetchListingWizardSnapshot,
  saveListingSection,
  saveListingStep,
  publishListing,
  isSilentFetchSnapshotArg,
} from './listingWizardThunks';

function normalizeSeoSavedSection<T extends Record<string, unknown>>(seo: T): T {
  if (typeof seo.slug !== 'string') return seo;
  const normalized = normalizeListingSlug(seo.slug);
  return normalized === seo.slug ? seo : { ...seo, slug: normalized };
}

function getCatalogSection(state: IListingWizardState): ListingCatalogSection {
  const raw = state.savedData.catalog as ListingCatalogSection | undefined;
  if (raw && Array.isArray(raw.selectedProductIds) && raw.assignments) {
    return raw;
  }
  const empty: ListingCatalogSection = {
    selectedProductIds: [],
    assignments: {},
  };
  state.savedData.catalog = empty;
  return empty;
}

function getVisualsSection(state: IListingWizardState): ListingVisualsSection {
  const raw = state.savedData.visuals as ListingVisualsSection | undefined;
  if (raw && Array.isArray(raw.gallery)) {
    return raw;
  }
  const empty: ListingVisualsSection = {
    gallery: [],
    mockupPreference: null,
    detailShots: [],
  };
  state.savedData.visuals = empty;
  return empty;
}

// ============================================
// INITIAL STATE
// ============================================

const initialState: IListingWizardState = {
  // Snapshot data
  design: null,
  availableProducts: [],
  currentListing: null,
  marketTaxonomy: null,

  // Wizard state
  currentStep: 0,
  completedSections: [],
  savedData: {},
  
  // Loading states
  isLoadingSnapshot: false,
  isSaving: false,
  isPublishing: false,
  
  // Error state
  error: null,
};

// ============================================
// SLICE (State Management)
// ============================================

const listingWizardSlice = createSlice({
  name: 'listingWizard',
  initialState,
  
  // ============================================
  // SYNCHRONOUS REDUCERS
  // ============================================
  reducers: {
    /**
     * Set current wizard step
     * 
     * Usage:
     *   dispatch(setCurrentStep(2))
     */
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    
    /**
     * Mark section as completed
     * 
     * Usage:
     *   dispatch(markSectionCompleted('identity'))
     */
    markSectionCompleted: (state, action: PayloadAction<IListingWizardSection>) => {
      if (!state.completedSections.includes(action.payload)) {
        state.completedSections.push(action.payload);
      }
    },
    
    /**
     * Update saved data (optimistic update)
     * 
     * Usage:
     *   dispatch(updateSavedData({ section: 'identity', data: {...} }))
     */
    updateSavedData: (
      state,
      action: PayloadAction<{
        section: IListingWizardSection;
        data: IListingWizardSectionData;
      }>
    ) => {
      const data =
        action.payload.section === 'seo'
          ? normalizeSeoSavedSection(action.payload.data as Record<string, unknown>)
          : action.payload.data;
      (state.savedData as any)[action.payload.section] = data;
    },

    /** Toggle product in catalog selection (reads latest Redux state — no stale closures). */
    toggleCatalogProduct: (
      state,
      action: PayloadAction<{
        productId: string;
        defaultColor?: string | null;
      }>
    ) => {
      const catalog = getCatalogSection(state);
      const { productId, defaultColor = null } = action.payload;
      const isSelected = catalog.selectedProductIds.includes(productId);

      if (isSelected) {
        catalog.selectedProductIds = catalog.selectedProductIds.filter(
          (id) => id !== productId
        );
        delete catalog.assignments[productId];

        const visuals = getVisualsSection(state);
        state.savedData.visuals = purgeVisualsForRemovedProduct(visuals, productId);
        return;
      }

      const productMeta = state.availableProducts.find((p) => p.id === productId);
      catalog.selectedProductIds = [...catalog.selectedProductIds, productId];
      catalog.assignments[productId] = catalog.assignments[productId] ?? {
        productId,
        surfaces: productMeta?.surfaces?.length
          ? productMeta.surfaces
          : ['Front'],
        defaultColor,
        defaultSide: 'front',
      };
    },

    /** Set default shop-card side (front/back) for one catalog product. */
    setCatalogProductDefaultSide: (
      state,
      action: PayloadAction<{ productId: string; side: 'front' | 'back' }>
    ) => {
      const catalog = getCatalogSection(state);
      const { productId, side } = action.payload;
      const existing = catalog.assignments[productId];
      if (!existing) return;

      catalog.assignments[productId] = {
        ...existing,
        defaultSide: side,
      };
    },

    /** Set default display color for one catalog product. */
    setCatalogProductDefaultColor: (
      state,
      action: PayloadAction<{ productId: string; colorKey: string | null }>
    ) => {
      const catalog = getCatalogSection(state);
      const { productId, colorKey } = action.payload;
      const existing = catalog.assignments[productId];
      if (!existing) return;

      catalog.assignments[productId] = {
        ...existing,
        defaultColor: colorKey,
      };
    },

    clearError: (state) => {
      state.error = null;
    },
    
    /**
     * Merge mockup URLs after generation (keeps wizard in sync without full reload)
     */
    updateDesignMockups: (
      state,
      action: PayloadAction<{
        frontMockupUrl?: string | null;
        backMockupUrl?: string | null;
        mockupUrlsByColor?: Record<string, { front?: string; back?: string }> | null;
        mockupVariantsByColor?: Record<string, unknown> | null;
      }>
    ) => {
      if (!state.design) return;
      const payload = action.payload;
      if (payload.frontMockupUrl !== undefined) {
        state.design.frontMockupUrl = payload.frontMockupUrl;
      }
      if (payload.backMockupUrl !== undefined) {
        state.design.backMockupUrl = payload.backMockupUrl;
      }
      if (payload.mockupUrlsByColor !== undefined) {
        state.design.mockupUrlsByColor = payload.mockupUrlsByColor;
      }
      if (payload.mockupVariantsByColor !== undefined) {
        state.design.mockupVariantsByColor = payload.mockupVariantsByColor as any;
      }
    },

    /**
     * Update design's customMockupsByProductAndColor (optimistic update)
     * Used when deleting a custom mockup to immediately update the UI
     * 
     * Usage:
     *   dispatch(updateDesignCustomMockups({ productId, color, side, mockupUrlToRemove: 'url' }))
     */
    updateDesignCustomMockups: (
      state,
      action: PayloadAction<{
        productId: string;
        color?: string;
        side?: 'front' | 'back';
        mockupUrlToRemove: string;
      }>
    ) => {
      if (!state.design?.customMockupsByProductAndColor) return;

      const { productId, color, side, mockupUrlToRemove } = action.payload;

      removeCustomMockupUrlFromTree(
        state.design.customMockupsByProductAndColor as Record<string, unknown>,
        productId,
        mockupUrlToRemove,
        { color, side }
      );
    },
    
    /**
     * Reset wizard state
     * 
     * Usage:
     *   dispatch(resetWizard())
     */
    resetWizard: () => initialState,
  },
  
  // ============================================
  // ASYNC THUNK HANDLERS (extraReducers)
  // ============================================
  extraReducers: (builder) => {
    // ========================================
    // FETCH WIZARD SNAPSHOT
    // ========================================
    builder
      .addCase(fetchListingWizardSnapshot.pending, (state, action) => {
        if (!isSilentFetchSnapshotArg(action.meta.arg)) {
          state.isLoadingSnapshot = true;
        }
        state.error = null;
      })
      .addCase(fetchListingWizardSnapshot.fulfilled, (state, action: PayloadAction<ListingWizardSnapshot>) => {
        if (!isSilentFetchSnapshotArg(action.meta.arg)) {
          state.isLoadingSnapshot = false;
        }
        
        // ✅ Enhanced: Migrate mockups to new structure if needed (backward compatibility)
        const design = action.payload.design;
        if (design?.customMockupsByProductAndColor) {
          const migrated = migrateMockupsToFitGenderStructure(design.customMockupsByProductAndColor);
          if (migrated) {
            design.customMockupsByProductAndColor = migrated as any;
          }
        }
        
        state.design = design;
        state.availableProducts = action.payload.catalog?.products || [];
        state.currentListing = action.payload.listing || null;
        state.marketTaxonomy = action.payload.taxonomy ?? null;
        
        // Restore current step from listing on initial load only — refetches keep local step
        const fetchArg = action.meta.arg as
          | string
          | { designId: string; preserveStep?: boolean; silent?: boolean };
        const preserveStep =
          typeof fetchArg === 'object' && fetchArg?.preserveStep === true;
        if (
          !preserveStep &&
          action.payload.listing?.currentStep !== undefined
        ) {
          state.currentStep = action.payload.listing.currentStep;
        }
        
        // Restore saved data from listing sections if it exists
        if (action.payload.listing?.sections) {
          state.savedData = action.payload.listing.sections as any;
          const seo = state.savedData.seo as { visibilityMode?: string; slug?: string } | undefined;
          if (seo) {
            seo.visibilityMode = normalizePublishDistribution(seo.visibilityMode);
            const normalizedSeo = normalizeSeoSavedSection(seo as Record<string, unknown>);
            if (normalizedSeo !== seo) {
              state.savedData.seo = normalizedSeo as typeof seo;
            }
          }

          // Mark completed sections based on what has data
          const sectionsWithData: IListingWizardSection[] = [];
          if (action.payload.listing.sections.identity) sectionsWithData.push('identity' as IListingWizardSection);
          if (action.payload.listing.sections.catalog) sectionsWithData.push('catalog' as IListingWizardSection);
          if (action.payload.listing.sections.visuals) sectionsWithData.push('visuals' as IListingWizardSection);
          if (action.payload.listing.sections.personalization) sectionsWithData.push('personalization' as IListingWizardSection);
          if (action.payload.listing.sections.pricing) sectionsWithData.push('pricing' as IListingWizardSection);
          if (action.payload.listing.sections.seo) sectionsWithData.push('seo' as IListingWizardSection);
          if (action.payload.listing.sections.review) sectionsWithData.push('review' as IListingWizardSection);
          state.completedSections = sectionsWithData;
        }
        
        state.error = null;
      })
      .addCase(fetchListingWizardSnapshot.rejected, (state, action) => {
        if (!isSilentFetchSnapshotArg(action.meta.arg)) {
          state.isLoadingSnapshot = false;
        }
        state.error = action.payload as string;
      });
    
    // ========================================
    // SAVE LISTING SECTION
    // ========================================
    builder
      .addCase(saveListingSection.pending, (state, action) => {
        const silent =
          typeof action.meta.arg === 'object' &&
          action.meta.arg !== null &&
          action.meta.arg.silent === true;
        if (!silent) {
          state.isSaving = true;
        }
        state.error = null;
      })
      .addCase(saveListingSection.fulfilled, (state, action) => {
        const silent =
          typeof action.meta.arg === 'object' &&
          action.meta.arg !== null &&
          action.meta.arg.silent === true;
        if (!silent) {
          state.isSaving = false;
        }
        const data =
          action.payload.section === 'seo'
            ? normalizeSeoSavedSection(action.payload.data as Record<string, unknown>)
            : action.payload.data;
        state.savedData[action.payload.section] = data;
        
        // Mark section as completed
        if (!state.completedSections.includes(action.payload.section)) {
          state.completedSections.push(action.payload.section);
        }
        
        // Update current listing with new sections data
        if (state.currentListing) {
          state.currentListing = {
            ...state.currentListing,
            sections: {
              ...state.currentListing.sections,
              [action.payload.section]: data,
            } as any,
            updatedAt: new Date().toISOString(),
          };
        } else if (action.payload.listing) {
          // If listing was created during save, store it
          state.currentListing = action.payload.listing as any;
        }
        
        state.error = null;
      })
      .addCase(saveListingSection.rejected, (state, action) => {
        const silent =
          typeof action.meta.arg === 'object' &&
          action.meta.arg !== null &&
          action.meta.arg.silent === true;
        if (!silent) {
          state.isSaving = false;
        }
        state.error = action.payload as string;
      });

    builder
      .addCase(saveListingStep.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(saveListingStep.fulfilled, (state, action) => {
        state.isSaving = false;
        if (state.currentListing) {
          state.currentListing = {
            ...state.currentListing,
            currentStep: action.payload.step,
          };
        }
        state.error = null;
      })
      .addCase(saveListingStep.rejected, (state, action) => {
        state.isSaving = false;
        state.error = (action.payload as string) || state.error;
      });
    
    // ========================================
    // PUBLISH LISTING
    // ========================================
    builder
      .addCase(publishListing.pending, (state) => {
        state.isPublishing = true;
        state.error = null;
      })
      .addCase(publishListing.fulfilled, (state, action) => {
        state.isPublishing = false;
        
        // Update listing status to published
        if (state.currentListing) {
          state.currentListing = {
            ...state.currentListing,
            status: 'published',
            publishAt: action.payload.publishedAt,
          };
        }
        
        state.error = null;
      })
      .addCase(publishListing.rejected, (state, action) => {
        state.isPublishing = false;
        state.error = action.payload as string;
      });
  },
});

// ============================================
// EXPORTS
// ============================================

// Export synchronous actions
export const {
  setCurrentStep,
  markSectionCompleted,
  updateSavedData,
  toggleCatalogProduct,
  setCatalogProductDefaultColor,
  setCatalogProductDefaultSide,
  clearError,
  updateDesignMockups,
  updateDesignCustomMockups,
  resetWizard,
} = listingWizardSlice.actions;

// Export reducer as default
export default listingWizardSlice.reducer;

